import { ExitToApp, Menu as MenuIcon, Person } from '@mui/icons-material';
import { AppBar, Box, Button, Drawer, Paper, FormControl, IconButton, List, ListItem, ListItemButton, ListItemText, Menu, MenuItem, Select, SelectChangeEvent, Toolbar, Typography, useTheme } from '@mui/material';
import React, { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useBranding } from '../../contexts/BrandingContext';
import { careRoles, clinicAdminLink, landingLinkFor, loginLink, profileLink, registerLink, selfRegistrationEnabled, toolbarButtons, } from '../../library/constants';
import { memberFullName } from '../../library/membershipDisplay';
import { isSupportedLanguage, writeLanguageChoice } from '../../library/models/language';
import { useIsCompact } from '../../library/useIsCompact';
import ResourcesIds from '../../resources/ResourcesIds';
import { useReactMutation } from '../../services/queryService';
import { isImpersonating, leaveImpersonation } from '../../services/sessionKeeper';
import { services } from '../../services/services';
import { anchorOrigin, buttonsContainerStyles, impersonationBannerStyles, impersonationTextStyles, buttonStyles, drawerItemStyles, drawerPaperStyles, getAppBarStyles, getLoginButtonStyles, getRegisterButtonStyles, iconStyles, languageSelectFormStyles, languageSelectStyles, logoImageStyles, logoStyles, rightContainerStyles, toolbarStyles, transformOrigin, welcomeTextStyles, wordmarkStyles } from './header.styles';
import { NotificationBell } from '../notifications/NotificationBell';
import { UserAvatar } from '../userAvatar/UserAvatar';

export const Header = (): JSX.Element => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { user, role, isAuthenticated, logout, updateUser } = useAuth();
    const { logoSrc, logoAlt, wordmark } = useBranding();
    const { t: translate } = useTranslation();

    const isMobile = useIsCompact();

    // A session that predates roles being stored reads as CUSTOMER rather than as
    // nothing: it keeps the whole clinic-facing nav it has always had, and still cannot
    // see anything reserved for the platform owner.
    const visibleButtons = toolbarButtons.filter(
        button => !button.roles || button.roles.includes(role ?? 'CUSTOMER')
    );

    /**
     * The bell, for the two roles that can be in an appointment. It is what carries the count
     * of booking requests a therapist has to answer, which used to sit on the My Calendar
     * entry: in the bar rather than in the nav, so it is in sight on a phone without opening
     * the drawer. Null reads as CUSTOMER, as it does for the nav above.
     */
    const hasBell: boolean = isAuthenticated && careRoles.includes(role ?? 'CUSTOMER');

    /** An administrator acting as one of their therapists, in this tab. */
    const actingAs: boolean = isAuthenticated && isImpersonating();

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const { i18n } = useTranslation();

    /*
     * `resolvedLanguage`, not `language`: the picker only has an option per supported
     * language, and a Select whose value matches no option renders empty. `language` is
     * whatever was detected and can carry a region ("ro-RO"); `resolvedLanguage` is the one
     * i18next actually settled on and loaded, so it is always one of the two on offer.
     *
     * Derived per render rather than held in state, which it used to be. `useTranslation`
     * already subscribes this component to `languageChanged`, so the value follows a change
     * made anywhere -- and something else does change it now: `useLanguagePreference` applies
     * a signed-in person's stored language at login. Seeded state would have shown the old
     * flag beside the new copy until something else re-rendered the header.
     */
    const language = i18n.resolvedLanguage ?? i18n.language;

    const updateLanguage = useReactMutation(services.users.updateLanguageMutation());

    const handleLanguageChange = (event: SelectChangeEvent<string>): void => {
        const next = event.target.value;

        if (!isSupportedLanguage(next) || next === language) {
            return;
        }

        i18n.changeLanguage(next);

        // Marked as a choice before anything else: from here on, no clinic default may
        // overrule it in this browser. See library/models/language.ts.
        writeLanguageChoice(next);

        if (!isAuthenticated || !user?.id) {
            return;
        }

        // Optimistic, and load-bearing rather than cosmetic: useLanguagePreference reads
        // `user.language` and would change the language straight back on the next render if
        // this still held the old one. It also rewrites localStorage.user, so a reload before
        // the request lands still comes up in the new language.
        updateUser({ language: next });

        // Fire and forget. A dropdown that raises an alert because a preference did not sync
        // is worse than one that quietly costs you the preference on your other device -- the
        // language has already changed on screen, so nothing the user can see is wrong. Same
        // trade AppointmentNotifier makes about a failed send never failing the booking.
        updateLanguage.mutate({ language: next }, {
            onError: () => undefined,
        });
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleDrawerToggle = () => {
        setDrawerOpen(prev => !prev);
    };

    const onLogoutClick = () => {
        logout();
        navigate('/home');
        handleMenuClose();
    };

    const onProfileClick = () => {
        navigate(profileLink);
        handleMenuClose();
    };

    const onHomeClick = () => {
        if (!isAuthenticated) {
            navigate('/home');
            return;
        }

        navigate(landingLinkFor(role));
    };

    return (
        <>
            <AppBar position="sticky" sx={getAppBarStyles(theme)}>
                <Toolbar sx={toolbarStyles}>
                    {/* Mobile Hamburger Button */}
                    {isMobile && isAuthenticated && (
                        <IconButton
                            edge="end"
                            color="inherit"
                            aria-label="menu"
                            onClick={handleDrawerToggle}
                            sx={{ ml: 1 }}
                        >
                            <MenuIcon />
                        </IconButton>
                    )}

                    {/* The clinic's logo when it has one, its NAME when it does not, and the
                        Carefull mark only where there is no clinic at all -- the platform's own
                        address, and the platform owner's session. Falling back to the mark on a
                        clinic's domain would put a brand their patients have never heard of above
                        their own appointments. See BrandingContextValue.wordmark. */}
                    <Box sx={logoStyles} onClick={onHomeClick}>
                        {wordmark
                            ? <Box sx={wordmarkStyles}>{wordmark}</Box>
                            : <Box component="img" src={logoSrc} alt={logoAlt} sx={logoImageStyles} />}
                    </Box>

                    {/* Desktop Toolbar Buttons */}
                    {!isMobile && isAuthenticated && (
                        <Box sx={buttonsContainerStyles}>
                            {visibleButtons.map(button => (
                                <Button
                                    key={button.text}
                                    onClick={() => navigate(button.link)}
                                    sx={buttonStyles}
                                >
                                    {translate(button.text)}
                                </Button>
                            ))}
                        </Box>
                    )}

                    {/* Right Side (Login/Profile) */}
                    <Box sx={rightContainerStyles}>
                        <FormControl size="small" variant="outlined" sx={languageSelectFormStyles}>
                            {/* The closed control shows the flag alone -- there is no room in the
                                bar for more -- so the only thing naming it for a screen reader,
                                or for a test looking it up by role, is this label. The open menu
                                spells each option out. */}
                            <Select
                                value={language}
                                onChange={handleLanguageChange}
                                inputProps={{ 'aria-label': translate(ResourcesIds.LANGUAGE_SELECT_LABEL) }}
                                renderValue={(value) => (value === 'ro' ? '🇷🇴' : '🇺🇸')}
                                sx={languageSelectStyles}
                            >
                                <MenuItem value="en">🇺🇸 {translate(ResourcesIds.LANGUAGE_EN)}</MenuItem>
                                <MenuItem value="ro">🇷🇴 {translate(ResourcesIds.LANGUAGE_RO)}</MenuItem>
                            </Select>
                        </FormControl>
                        {isAuthenticated ? (
                            <>
                                <Typography variant="body2" sx={welcomeTextStyles}>
                                    {`${translate(ResourcesIds.WELCOME)} ${user?.firstName || user?.emailAddress}`}!
                                </Typography>
                                {hasBell && <NotificationBell />}
                                <IconButton size="large" onClick={handleMenuOpen}>
                                    <UserAvatar person={user} size="md" />
                                </IconButton>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={handleMenuClose}
                                    transformOrigin={transformOrigin}
                                    anchorOrigin={anchorOrigin}
                                >
                                    {/* Everything on Profile is the therapist's own account,
                                        which the API keeps out of an administrator's reach. */}
                                    {!actingAs && (
                                        <MenuItem onClick={onProfileClick}>
                                            <Person sx={iconStyles} />
                                            {translate(ResourcesIds.PROFILE)}
                                        </MenuItem>
                                    )}
                                    <MenuItem onClick={onLogoutClick}>
                                        <ExitToApp sx={iconStyles} />
                                        {translate(ResourcesIds.LOGOUT)}
                                    </MenuItem>
                                </Menu>
                            </>
                        ) : (
                            <>
                                <Button
                                    onClick={() => navigate(loginLink)}
                                    sx={getLoginButtonStyles(theme)}
                                    variant="outlined"
                                >
                                    {translate(ResourcesIds.LOGIN)}
                                </Button>
                                {/* Hidden, not gone -- see selfRegistrationEnabled. */}
                                {selfRegistrationEnabled && (
                                    <Button
                                        onClick={() => navigate(registerLink)}
                                        sx={getRegisterButtonStyles(theme)}
                                        variant="contained"
                                    >
                                        {translate(ResourcesIds.REGISTER)}
                                    </Button>
                                )}
                            </>
                        )}
                    </Box>

                    {/* Drawer for Mobile Menu */}
                    <Drawer
                        anchor="left"
                        open={drawerOpen}
                        onClose={handleDrawerToggle}
                        PaperProps={{ sx: drawerPaperStyles }}
                    >
                        <List>
                            {visibleButtons.map(button => (
                                <ListItem key={button.text} disablePadding>
                                    <ListItemButton
                                        onClick={() => {
                                            navigate(button.link);
                                            setDrawerOpen(false);
                                        }}
                                        sx={drawerItemStyles}
                                    >
                                        <ListItemText primary={translate(button.text)} />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    </Drawer>
                </Toolbar>
            </AppBar>
            {/* Floating rather than a row in the bar: the bar's height is what everything sticky
                clears (APP_BAR_HEIGHT), and a row that came and went would slide under it. And
                outside it, because the bar's backdrop-filter would make `fixed` mean the bar. */}
            {actingAs && (
                <Paper role="status" elevation={6} sx={impersonationBannerStyles}>
                    <Typography variant="body2" sx={impersonationTextStyles}>
                        {translate(ResourcesIds.IMPERSONATION_BANNER, {
                            name: (user && memberFullName(user)) || user?.emailAddress,
                        })}
                    </Typography>
                    <Button size="small" variant="contained" color="secondary"
                        onClick={() => leaveImpersonation(clinicAdminLink)}>
                        {translate(ResourcesIds.IMPERSONATION_RETURN)}
                    </Button>
                </Paper>
            )}
        </>
    );
};
